import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Truck, Wallet } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import ProductCard from "../components/ProductCard";
import { Button } from "../components/ui/button";

const HERO_IMG =
  "https://images.unsplash.com/photo-1581182800629-7d90925ad072?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHx3b21hbiUyMG5hdHVyYWwlMjBtYWtldXB8ZW58MHx8fHwxNzgzNTQxMDQ0fDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { t, lang, isRTL } = useLang();
  const [featured, setFeatured] = useState([]);
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then(({ data }) => setFeatured(data.slice(0, 6)));
    api.get("/products", { params: { on_offer: true } }).then(({ data }) => setOffers(data.slice(0, 4)));
  }, []);

  const hero = useMemo(() => featured[0], [featured]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-16 grid md:grid-cols-12 gap-8 md:gap-12 items-end">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="md:col-span-6"
          >
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#7B5CBF] mb-6">
              {t("heroKicker")}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#1A1738]">
              {t("heroTitle")}
            </h1>
            <p className="mt-6 text-[#767676] text-base md:text-lg max-w-md leading-relaxed">
              {t("heroSubtitle")}
            </p>
            <div className="mt-8 flex gap-3">
              <Link to="/shop">
                <Button
                  data-testid="hero-shop-btn"
                  className="rounded-full bg-[#1A1738] hover:bg-[#2C2650] text-white px-6 h-11"
                >
                  {t("shopNow")}
                  <ArrowRight className={`h-4 w-4 ${isRTL ? "me-2 rotate-180" : "ms-2"}`} strokeWidth={1.5} />
                </Button>
              </Link>
              <Link to="/shop?on_offer=true">
                <Button
                  variant="outline"
                  data-testid="hero-offers-btn"
                  className="rounded-full border-[#1A1738] text-[#1A1738] h-11 hover:bg-[#F3F0EB]"
                >
                  {t("exploreOffers")}
                </Button>
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-4 max-w-md">
              <Feature icon={Truck} label={lang === "ar" ? "شحن سريع" : "Fast delivery"} />
              <Feature icon={Wallet} label={lang === "ar" ? "دفع بالمحفظة" : "Wallet checkout"} />
              <Feature icon={ShieldCheck} label={lang === "ar" ? "أصلي 100%" : "100% authentic"} />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="md:col-span-6 relative"
          >
            {/* Prestige panel: deep navy with brand gradient + logo */}
            <div className="relative aspect-[4/5] rounded-lg overflow-hidden bg-[#0F0E2E]">
              {/* soft radial glow behind */}
              <div className="pointer-events-none absolute inset-0 opacity-70 z-0"
                style={{ background: "radial-gradient(70% 55% at 68% 42%, rgba(123,92,191,0.55), transparent 60%), radial-gradient(45% 40% at 32% 78%, rgba(233,181,121,0.35), transparent 65%)" }}
              />
              {/* tiny stars */}
              <div className="pointer-events-none absolute inset-0 z-0">
                {[[12,22],[74,18],[26,68],[62,82],[88,52],[42,42]].map(([x,y],i) => (
                  <span key={i} className="absolute h-[3px] w-[3px] rounded-full bg-white/80 animate-pulse"
                    style={{ left:`${x}%`, top:`${y}%`, animationDelay:`${i*0.4}s` }} />
                ))}
              </div>
              <img
                src={HERO_IMG}
                alt="Featured beauty"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <img
                src="/nebula-logo.jpg"
                alt="Nebula Prestige"
                className="absolute top-6 start-6 h-14 w-14 rounded-md object-cover shadow-lg"
              />
              {hero && (
                <Link
                  to={`/product/${hero.id}`}
                  data-testid="hero-product-link"
                  className="absolute bottom-6 start-6 end-6 md:end-auto md:max-w-xs bg-white/92 backdrop-blur-md p-5 rounded-md hover:bg-white transition-colors"
                >
                  <div className="text-[10px] uppercase tracking-widest text-[#7B5CBF]">
                    {hero.brand}
                  </div>
                  <div className="font-display text-lg leading-snug mt-1 text-[#1A1738]">
                    {lang === "ar" ? hero.name_ar : hero.name_en}
                  </div>
                  <div className="mt-2 text-sm text-[#1A1738] font-medium">
                    ${hero.final_price.toFixed(2)}
                  </div>
                </Link>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16" data-testid="featured-section">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#7B5CBF]">{t("featured")}</div>
            <h2 className="font-display text-3xl md:text-4xl mt-2">
              {lang === "ar" ? "الأكثر طلباً هذا الموسم" : "Most requested this season"}
            </h2>
          </div>
          <Link to="/shop" className="hover-underline text-sm hidden md:block" data-testid="featured-see-all">
            {t("seeAll")}
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
          {featured.slice(0, 4).map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* OFFERS */}
      {offers.length > 0 && (
        <section className="bg-[#F3F0EB] py-16" data-testid="offers-section">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex items-end justify-between mb-8">
              <div>
                <div className="text-[11px] uppercase tracking-[0.28em] text-[#7B5CBF]">{t("onOffer")}</div>
                <h2 className="font-display text-3xl md:text-4xl mt-2">
                  {lang === "ar" ? "عروض هذا الأسبوع" : "This week’s offers"}
                </h2>
              </div>
              <Link to="/shop?on_offer=true" className="hover-underline text-sm hidden md:block" data-testid="offers-see-all">
                {t("seeAll")}
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {offers.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer strip */}
      <footer className="border-t border-[#E5E1D8] py-10 mt-8">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-xs text-[#767676] flex flex-wrap gap-4 justify-between">
          <div>© {new Date().getFullYear()} {t("brand")}</div>
          <div>{t("tagline")}</div>
        </div>
      </footer>
    </main>
  );
}

function Feature({ icon: Icon, label }) {
  return (
    <div className="flex flex-col items-start gap-1.5">
      <Icon className="h-4 w-4 text-[#7B5CBF]" strokeWidth={1.5} />
      <span className="text-xs text-[#1A1738]">{label}</span>
    </div>
  );
}
